// src/components/HotelCard.jsx
// ═══════════════════════════════════════════════════════════════════════════════
// AIRGATI HOTEL CARD - With Distance, Amenities, Star Rating
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState } from 'react';
import './HotelCard.css';

const HotelCard = ({ hotel, onBook, isBestDeal = false }) => {
  const [expanded, setExpanded] = useState(false);

  // Safety check - if hotel is invalid, don't render
  if (!hotel || typeof hotel !== 'object') {
    return null;
  }

  // Format price safely
  const formatPrice = (price) => {
    if (price === null || price === undefined || isNaN(price)) return null;
    return Math.round(Number(price));
  };

  // Star rating display
  const renderStars = (stars) => {
    const numStars = Math.min(Number(stars) || 0, 5);
    return '⭐'.repeat(numStars);
  };

  // Safe string conversion - NEVER render objects
  const safeString = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return String(value);
    if (typeof value === 'object') return ''; // Don't render objects - return empty!
    return String(value);
  };

  // Handle booking click
  const handleBookClick = (link) => {
    if (onBook) {
      onBook({ hotel, bookingLink: link });
    }
    if (link?.url) {
      window.open(link.url, '_blank', 'noopener,noreferrer');
    }
  };

  // Safe amenities - handle both string arrays and object arrays
  const safeAmenities = Array.isArray(hotel.amenities) 
    ? hotel.amenities.map(a => typeof a === 'string' ? a : (a?.name || a?.label || '')).filter(Boolean)
    : [];

  // Safe booking links
  const safeBookingLinks = Array.isArray(hotel.bookingLinks) 
    ? hotel.bookingLinks.filter(link => link && typeof link === 'object')
    : [];

  const hotelName = safeString(hotel.name) || 'Hotel';
  const hotelCity = safeString(hotel.city) || '';
  const hotelLocation = safeString(hotel.location?.downtownName) || hotelCity || '';
  const hotelStars = Number(hotel.stars) || 0;
  const hotelRating = hotel.rating ? parseFloat(hotel.rating) : null;
  const pricePerNight = formatPrice(hotel.pricePerNight);
  const totalPrice = formatPrice(hotel.totalPrice);
  const nights = hotel.nights ? Number(hotel.nights) : null;
  
  // Distance info - safely extract from location object
  const distanceToDowntown = typeof hotel.location?.distanceToDowntown === 'number' ? hotel.location.distanceToDowntown : null;
  const distanceToAirport = typeof hotel.location?.distanceToAirport === 'number' ? hotel.location.distanceToAirport : null;
  const downtownName = safeString(hotel.location?.downtownName) || 'Downtown';
  const airportName = safeString(hotel.location?.airportName) || 'Airport';

  // Generate star-based color for placeholder
  const getPlaceholderGradient = () => {
    if (hotelStars >= 5) return 'linear-gradient(135deg, #fbbf24, #f59e0b)';
    if (hotelStars >= 4) return 'linear-gradient(135deg, #667eea, #764ba2)';
    if (hotelStars >= 3) return 'linear-gradient(135deg, #10b981, #059669)';
    return 'linear-gradient(135deg, #6b7280, #4b5563)';
  };

  return (
    <div className={`hotel-card ${expanded ? 'expanded' : ''} ${isBestDeal ? 'best-deal' : ''}`}>
      {/* Hotel Image */}
      <div className="hotel-card-image" style={{ background: getPlaceholderGradient() }}>
        {hotel.image ? (
          <img src={hotel.image} alt={hotelName} loading="lazy" onError={(e) => { e.target.style.display = 'none'; }} />
        ) : (
          <div className="hotel-placeholder">🏨</div>
        )}
        {hotelStars > 0 && (
          <div className="hotel-stars-badge">{renderStars(hotelStars)}</div>
        )}
      </div>

      {/* Hotel Content */}
      <div className="hotel-card-content">
        {/* Header: Name, Location, Rating */}
        <div className="hotel-header">
          <div className="hotel-name-location">
            <h3 className="hotel-name">{hotelName}</h3>
            <p className="hotel-location">📍 {hotelLocation}{hotelCity && hotelLocation !== hotelCity ? `, ${hotelCity}` : ''}</p>
          </div>
          
          {hotelRating && (
            <div className="hotel-rating">
              <span className="rating-score">{hotelRating}</span>
              <span className="rating-label">
                {hotelRating >= 4.5 ? 'Excellent' : hotelRating >= 4 ? 'Very Good' : hotelRating >= 3.5 ? 'Good' : 'Fair'}
              </span>
            </div>
          )}
        </div>

        {/* Amenities */}
        {safeAmenities.length > 0 && (
          <div className="hotel-amenities">
            {safeAmenities.slice(0, 6).map((amenity, idx) => (
              <span key={idx} className="amenity-tag">{amenity}</span>
            ))}
          </div>
        )}

        {/* Distance Info */}
        {(distanceToDowntown !== null || distanceToAirport !== null) && (
          <div className="hotel-distance">
            {distanceToDowntown !== null && (
              <span className="distance-tag">
                🏙️ {distanceToDowntown.toFixed(1)} mi to {downtownName}
              </span>
            )}
            {distanceToAirport !== null && (
              <span className="distance-tag">
                ✈️ {distanceToAirport.toFixed(1)} mi to {airportName}
              </span>
            )}
          </div>
        )}

        {/* Price and Booking */}
        <div className="hotel-footer">
          <div className="hotel-price-section">
            {pricePerNight ? (
              <>
                <div className="hotel-price">
                  <span className="price-amount">${pricePerNight}</span>
                  <span className="price-period">/night</span>
                </div>
                {totalPrice && nights && (
                  <div className="hotel-total">
                    ${totalPrice} total for {nights} nights
                  </div>
                )}
              </>
            ) : (
              <span className="price-check">Check prices →</span>
            )}
          </div>

          <button 
            className="hotel-expand-btn"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? 'Less options' : 'Book Now →'} 
          </button>
        </div>
      </div>

      {/* Expanded Booking Options */}
      {expanded && safeBookingLinks.length > 0 && (
        <div className="hotel-booking-options">
          <div className="booking-options-header">
            <span>🔍 Compare prices across sites</span>
          </div>
          <div className="booking-options-list">
            {safeBookingLinks.map((link, idx) => (
              <button
                key={idx}
                className="booking-option"
                onClick={() => handleBookClick(link)}
              >
                {link.logo && (
                  <img 
                    src={link.logo} 
                    alt={link.name} 
                    className="option-logo-img"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
                <span className="option-name">{safeString(link.name) || 'Book'}</span>
                <span className="option-action">Book →</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default HotelCard;
